/**
 * 인증 세션 유지 (액세스 토큰 재발급 · 세션 종료)
 *
 * - 401 → 리프레시 토큰으로 액세스 토큰 재발급 → 원래 요청 1회 재시도
 * - 리프레시 토큰까지 만료/무효 → 토큰 정리 후 로그인 화면으로 이동 (경로 무관)
 * - 네트워크 오류·게이트웨이 장애는 "일시적 실패"로 보고 로그아웃하지 않음
 *
 * apiClient 와 apiClient 를 거치지 않는 직접 fetch 호출부가 함께 쓴다.
 * (apiClient 를 import 하지 않아 순환 의존성이 없다)
 */

import { getAuthRefreshUrl } from '../config/apiBaseUrl';
import { normalizeBearerTokenForAuthorization } from '../utils/bearerTokenNormalize';
import { ROUTES } from '../lib/constants/routes';
import { clearLinkedAccountSessions, removeLinkedAccountSession } from '../utils/linkedAccountSessions';

const ACCESS_TOKEN_STORAGE_KEY = 'medicrew_access_token';
const REFRESH_TOKEN_STORAGE_KEY = 'medicrew_refresh_token';
const USER_STORAGE_KEY = 'medicrew_user';
const LINKED_ACCOUNTS_STORAGE_KEY = 'medicrew_linked_accounts';

/**
 * - refreshed: 새 액세스 토큰 저장 완료
 * - expired: 리프레시 토큰이 없거나 만료/무효 → 세션 종료(로그인 필요)
 * - failed: 네트워크 오류·게이트웨이 장애 등 일시적 실패 → 세션 유지
 */
export type SessionRefreshResult = 'refreshed' | 'expired' | 'failed';

/** 일시적 장애로 보는 응답 코드 (그 외 비정상 응답은 리프레시 토큰이 유효하지 않은 것으로 본다) */
const TRANSIENT_REFRESH_STATUSES = [408, 429, 502, 503, 504];

/**
 * 동시 401 폭주 시 리프레시 POST가 여러 번 나가 세션이 깨지는 것을 막기 위한 단일 비행 갱신.
 * 계정 전환으로 리프레시 토큰이 바뀌면 다른 계정의 갱신이므로 재사용하지 않는다.
 */
let refreshInFlight: { refreshToken: string; promise: Promise<SessionRefreshResult> } | null = null;

export function hasStoredSessionTokens(): boolean {
  const accessToken = localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY)?.trim();
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY)?.trim();
  return Boolean(accessToken) || Boolean(refreshToken);
}

async function requestNewAccessToken(refreshToken: string): Promise<SessionRefreshResult> {
  let response: Response;
  try {
    response = await fetch(getAuthRefreshUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });
  } catch {
    return 'failed';
  }

  if (!response.ok) {
    return TRANSIENT_REFRESH_STATUSES.includes(response.status) ? 'failed' : 'expired';
  }

  // OpenAPI: CreateAccessTokenByRefreshTokenResponseDto — accessToken만 반환
  const responseBody = (await response.json().catch(() => null)) as { accessToken?: string } | null;
  if (!responseBody?.accessToken) {
    return 'failed';
  }

  // 응답을 기다리는 사이 계정이 전환됐다면(리프레시 토큰이 바뀜) 이전 계정의 토큰을 덮어쓰지 않는다
  if (localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY)?.trim() !== refreshToken) {
    return 'failed';
  }

  localStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, normalizeBearerTokenForAuthorization(responseBody.accessToken));
  return 'refreshed';
}

/** 현재 선택된 계정의 리프레시 토큰으로 액세스 토큰 재발급 (같은 토큰으로 동시 호출 시 실제 요청은 한 번만) */
export function refreshSession(): Promise<SessionRefreshResult> {
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY)?.trim();
  if (!refreshToken) {
    return Promise.resolve('expired');
  }

  if (refreshInFlight?.refreshToken === refreshToken) {
    return refreshInFlight.promise;
  }

  const promise = requestNewAccessToken(refreshToken).finally(() => {
    if (refreshInFlight?.promise === promise) {
      refreshInFlight = null;
    }
  });
  refreshInFlight = { refreshToken, promise };
  return promise;
}

interface StoredUserSnapshot {
  currentAccount?: { accountId?: string; loginId?: string };
  accounts?: unknown[];
}

function readStoredUserSnapshot(): StoredUserSnapshot | null {
  try {
    const raw = localStorage.getItem(USER_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as StoredUserSnapshot) : null;
  } catch {
    return null;
  }
}

function redirectTo(path: string): void {
  if (typeof window === 'undefined') return;
  window.location.href = path;
}

/**
 * 세션 종료 (리프레시 토큰까지 만료/무효).
 *
 * - 연결된 계정이 여러 개: 만료된 현재 계정의 로그인 정보만 지우고, 그 계정으로 다시 로그인하는 화면으로 이동.
 *   (다른 연결 계정은 로그인 상태 유지 — 계정별 세션은 독립적)
 * - 계정이 하나뿐: 토큰·유저·연결 계정 정보를 모두 정리하고 로그인 화면으로 이동.
 * 재로그인 후 직전 화면으로 돌아올 수 있도록 현재 경로를 redirect 로 전달한다.
 */
export function endSessionAndRedirectToLogin(): void {
  const storedUser = readStoredUserSnapshot();
  const expiredAccountId = storedUser?.currentAccount?.accountId;
  const expiredLoginId = storedUser?.currentAccount?.loginId;
  const hasOtherLinkedAccounts = (storedUser?.accounts?.length ?? 0) > 1;

  localStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
  localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);

  if (hasOtherLinkedAccounts && expiredAccountId && expiredLoginId) {
    removeLinkedAccountSession(expiredAccountId);
    // 이미 해당 화면에 있으면 이동하지 않음 (무한 루프 방지)
    if (typeof window !== 'undefined' && window.location.pathname !== ROUTES.AUTH.ADD_ACCOUNT) {
      const currentPath = window.location.pathname + window.location.search;
      redirectTo(
        `${ROUTES.AUTH.ADD_ACCOUNT}?reason=token_expired&relogin=${encodeURIComponent(expiredLoginId)}&redirect=${encodeURIComponent(currentPath)}`
      );
    }
    return;
  }

  localStorage.removeItem(USER_STORAGE_KEY);
  // 연결된 계정(계정 전환용) 토큰도 로그아웃과 동일하게 모두 정리
  localStorage.removeItem(LINKED_ACCOUNTS_STORAGE_KEY);
  clearLinkedAccountSessions();

  if (typeof window === 'undefined') return;
  // 이미 로그인 페이지에 있으면 이동하지 않음 (무한 루프 방지)
  if (window.location.pathname.startsWith('/login')) return;

  const currentPath = window.location.pathname + window.location.search;
  redirectTo(`/login?redirect=${encodeURIComponent(currentPath)}&reason=token_expired`);
}

/**
 * apiClient 를 거치지 않는 fetch 호출부용.
 * 401 이면 토큰 재발급 후 1회 재시도하고, 리프레시 토큰까지 만료됐으면 로그인 화면으로 보낸다.
 *
 * @param sendRequest 호출 때마다 최신 액세스 토큰으로 헤더를 만들어 fetch 해야 한다 (재시도 시 새 토큰 사용)
 */
export async function fetchWithSessionRefresh(sendRequest: () => Promise<Response>): Promise<Response> {
  const response = await sendRequest();
  if (response.status !== 401 || !hasStoredSessionTokens()) {
    return response;
  }

  const refreshResult = await refreshSession();
  if (refreshResult === 'refreshed') {
    return sendRequest();
  }
  if (refreshResult === 'expired') {
    endSessionAndRedirectToLogin();
  }
  return response;
}
